import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck, ArrowRight } from 'lucide-react'
import { getAuthenticatedUserProfile } from '@/services/api'
import { isAdminEligible } from '@/types/api'
import { useActingAs } from '@/state/actingAs'
import { useAuth } from '@/contexts/AuthContext'
import { isFrontFlowOrg } from '@/constants/frontflowOrg'

/**
 * Shortcut into the admin tool, shown only to root/partner organizations —
 * and never while acting as another org, since the point of acting as is to
 * see the product exactly as that org would, and no ordinary org sees this.
 * Hidden for FrontFlow itself: its account is used for demos, and Admin is in
 * the user menu anyway.
 */
export function AdminShortcutPanel() {
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getAuthenticatedUserProfile,
  })
  const actingAs = useActingAs()
  const { user } = useAuth()

  if (actingAs) return null
  if (isFrontFlowOrg(user?.tenantId)) return null
  if (!isAdminEligible(profile?.user.organization)) return null

  return (
    <Link
      to="/admin"
      className="flex items-center justify-between gap-4 rounded-panel border border-canvas-border bg-surface px-5 py-4 shadow-panel transition-colors hover:bg-white"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white">
          <ShieldCheck className="h-4.5 w-4.5" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Admin</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Manage organizations and workflows
          </p>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" />
    </Link>
  )
}
